import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Chip,
    Paper,
    TextField,
    MenuItem,
    InputAdornment,
    IconButton,
    Collapse,
    Card,
    CardContent,
    Tooltip,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
    Add,
    FileDownload,
    Category as CategoryIcon,
    Search,
    Clear,
    ExpandMore,
    ExpandLess,
    TrendingDown,
    Receipt,
    Analytics,
    Payment,
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ServerSideDataTable } from "../../components/common/DataTable";
import { ExpenseForm } from "../../components/expenses/ExpenseForm";
import { ExpenseCategoryManager } from "../../components/expenses/ExpenseCategoryManager";
import { expenseService } from "../../services/expenseService";
import { formatCurrencyBDT } from "../../utils/currency";
import {
    Expense,
    ExpenseSearchParams,
    ExpenseCategory,
    ExpenseStatisticsResponse,
    PaymentMethod,
} from "../../types";
import {
    EXPENSE_PAYMENT_METHODS,
    getPaymentMethodColor,
} from "../../utils/paymentMethods";

// ─── Constants ───────────────────────────────────────────────────────────────

type DatePreset = "today" | "this_week" | "this_month" | "last_month" | "this_year" | "all";

interface DateRange {
    from: Date | null;
    to: Date | null;
}

function getPresetRange(preset: DatePreset): DateRange {
    const now = new Date();
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    switch (preset) {
        case "today": {
            const d = startOf(now);
            return { from: d, to: now };
        }
        case "this_week": {
            const day = now.getDay();
            const monday = new Date(now);
            monday.setDate(now.getDate() - ((day + 6) % 7));
            return { from: startOf(monday), to: now };
        }
        case "this_month":
            return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
        case "last_month": {
            const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const last = new Date(now.getFullYear(), now.getMonth(), 0);
            return { from: first, to: last };
        }
        case "this_year":
            return { from: new Date(now.getFullYear(), 0, 1), to: now };
        case "all":
        default:
            return { from: null, to: null };
    }
}

function toISODate(d: Date | null, endOfDay = false): string | undefined {
    if (!d) return undefined;
    const date = d.toISOString().split("T")[0];
    return endOfDay ? `${date}T23:59:59` : `${date}T00:00:00`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface KpiCardProps {
    label: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    sub?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, color, sub }) => (
    <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent sx={{ pb: "16px !important" }}>
            {/* Icon above the number on a phone, so large totals aren't cut off. */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "flex-start", gap: { xs: 1, sm: 1.5 } }}>
                <Box
                    sx={{
                        bgcolor: `${color}.light`,
                        color: `${color}.dark`,
                        borderRadius: 1.5,
                        p: 0.75,
                        display: "flex",
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                    <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
                        {value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {label}
                    </Typography>
                    {sub && (
                        <Typography variant="caption" color="text.disabled">
                            {sub}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const ExpenseList: React.FC = () => {
    // ── List state ──────────────────────────────────────────────────────────
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [totalRows, setTotalRows] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

    // ── Stats state ─────────────────────────────────────────────────────────
    const [stats, setStats] = useState<ExpenseStatisticsResponse | null>(null);
    const [statsOpen, setStatsOpen] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);

    // ── Category state ──────────────────────────────────────────────────────
    const [allCategories, setAllCategories] = useState<ExpenseCategory[]>([]);

    // ── Filter state ────────────────────────────────────────────────────────
    const [searchText, setSearchText] = useState("");
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
    const [datePreset, setDatePreset] = useState<DatePreset>("this_month");
    const [customRange, setCustomRange] = useState<DateRange>({ from: null, to: null });
    const [exporting, setExporting] = useState(false);

    // ── Dialog state ────────────────────────────────────────────────────────
    const [formOpen, setFormOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

    const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Derived filter params ────────────────────────────────────────────────
    const dateRange: DateRange =
        datePreset === "all" ? { from: null, to: null } : getPresetRange(datePreset);

    const searchParams: ExpenseSearchParams = {
        ...(searchText.trim() ? { search_query: searchText.trim() } : {}),
        ...(selectedCategories.length ? { category_ids: selectedCategories } : {}),
        ...(paymentMethod ? { payment_method: paymentMethod } : {}),
        ...(dateRange.from ? { expense_date_from: toISODate(dateRange.from) } : {}),
        ...(dateRange.to ? { expense_date_to: toISODate(dateRange.to, true) } : {}),
    };

    // ── Data fetching ────────────────────────────────────────────────────────
    const fetchExpenses = useCallback(async (params: ExpenseSearchParams, page: number, pageSize: number) => {
        try {
            setLoading(true);
            setError("");
            const response = await expenseService.searchExpenses(params, page * pageSize, pageSize);
            setExpenses(response.items);
            setTotalRows(response.total);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to load expenses");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchStats = useCallback(async (params: ExpenseSearchParams) => {
        try {
            setStatsLoading(true);
            const data = await expenseService.getExpenseStatistics(params);
            setStats(data);
        } catch {
            // stats failure is non-critical
        } finally {
            setStatsLoading(false);
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const cats = await expenseService.getActiveCategories();
            setAllCategories(cats);
        } catch {
            // ignore
        }
    }, []);

    // ── Effects ──────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchCategories();
        // apply default this_month preset on mount
    }, [fetchCategories]);

    useEffect(() => {
        fetchExpenses(searchParams, paginationModel.page, paginationModel.pageSize);
        fetchStats(searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        searchText, selectedCategories, paymentMethod,
        datePreset, customRange,
        paginationModel.page, paginationModel.pageSize,
    ]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleSearchChange = (val: string) => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        searchDebounce.current = setTimeout(() => setSearchText(val), 400);
    };

    const handlePreset = (preset: DatePreset) => {
        setDatePreset(preset);
        setPaginationModel((p) => ({ ...p, page: 0 }));
    };

    const handleCategoryToggle = (id: string) => {
        setSelectedCategories((prev) =>
            prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
        );
        setPaginationModel((p) => ({ ...p, page: 0 }));
    };

    const handleClearFilters = () => {
        setSearchText("");
        setSelectedCategories([]);
        setPaymentMethod("");
        setDatePreset("this_month");
        setCustomRange({ from: null, to: null });
        setPaginationModel({ page: 0, pageSize: 50 });
    };

    const handleAddExpense = () => {
        setSelectedExpense(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEditExpense = (expense: Expense) => {
        setSelectedExpense(expense);
        setFormMode("edit");
        setFormOpen(true);
    };

    const handleDeleteExpense = async (expenseId: string) => {
        if (!window.confirm("Delete this expense? This cannot be undone.")) return;
        try {
            await expenseService.deleteExpense(expenseId);
            fetchExpenses(searchParams, paginationModel.page, paginationModel.pageSize);
            fetchStats(searchParams);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to delete expense");
        }
    };

    const handleFormSuccess = () => {
        setFormOpen(false);
        setSelectedExpense(null);
        fetchExpenses(searchParams, paginationModel.page, paginationModel.pageSize);
        fetchStats(searchParams);
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const blob = await expenseService.exportExpenses(searchParams);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `expenses_${new Date().toISOString().split("T")[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns: GridColDef[] = [
        {
            field: "expense_date",
            headerName: "Date",
            width: 110,
            valueGetter: (value) => new Date(value).toLocaleDateString("en-GB"),
        },
        {
            field: "category",
            headerName: "Category",
            width: 150,
            valueGetter: (value: ExpenseCategory) => value?.name || "—",
        },
        {
            field: "amount",
            headerName: "Amount",
            width: 130,
            renderCell: (params) => (
                <Typography fontWeight={600} color="error.main" fontSize="0.875rem">
                    {formatCurrencyBDT(parseFloat(params.value))}
                </Typography>
            ),
        },
        {
            field: "payment_method",
            headerName: "Method",
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={getPaymentMethodColor(params.value)}
                    size="small"
                />
            ),
        },
        {
            field: "vendor_name",
            headerName: "Vendor",
            flex: 1,
            minWidth: 140,
            valueGetter: (value) => value || "—",
        },
        {
            field: "reference_number",
            headerName: "Reference",
            width: 120,
            valueGetter: (value) => value || "—",
        },
        {
            field: "notes",
            headerName: "Notes",
            flex: 1,
            minWidth: 160,
            valueGetter: (value) => value || "—",
        },
    ];

    // ── Preset labels ─────────────────────────────────────────────────────────
    const presets: { key: DatePreset; label: string }[] = [
        { key: "today", label: "Today" },
        { key: "this_week", label: "This Week" },
        { key: "this_month", label: "This Month" },
        { key: "last_month", label: "Last Month" },
        { key: "this_year", label: "This Year" },
        { key: "all", label: "All Time" },
    ];

    const hasActiveFilters =
        searchText || selectedCategories.length || paymentMethod || datePreset !== "this_month";

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h4" component="h1" fontWeight={700}>
                        Expenses
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Button variant="outlined" size="small" startIcon={<CategoryIcon />} onClick={() => setCategoryManagerOpen(true)}>
                            Categories
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<FileDownload />} onClick={handleExport} disabled={exporting}>
                            {exporting ? "Exporting…" : "Export CSV"}
                        </Button>
                        <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAddExpense}>
                            Add Expense
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {/* KPI Cards */}
                {stats && (
                    <Box sx={{ mb: 2 }}>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <KpiCard
                                    label="Total Spent"
                                    value={formatCurrencyBDT(stats.total_amount)}
                                    icon={<TrendingDown fontSize="small" />}
                                    color="error"
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <KpiCard
                                    label="Transactions"
                                    value={stats.total_expenses}
                                    icon={<Receipt fontSize="small" />}
                                    color="primary"
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <KpiCard
                                    label="Avg per Expense"
                                    value={formatCurrencyBDT(stats.average_expense)}
                                    icon={<Analytics fontSize="small" />}
                                    color="info"
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <KpiCard
                                    label="Top Category"
                                    value={stats.category_breakdown[0]?.label ?? "—"}
                                    sub={stats.category_breakdown[0] ? formatCurrencyBDT(stats.category_breakdown[0].value) : undefined}
                                    icon={<CategoryIcon fontSize="small" />}
                                    color="warning"
                                />
                            </Grid>
                        </Grid>

                        {/* Collapsible breakdown */}
                        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                            <Box
                                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1, cursor: "pointer" }}
                                onClick={() => setStatsOpen((v) => !v)}
                            >
                                <Typography variant="subtitle2" color="text.secondary">
                                    Breakdown by Category &amp; Payment Method
                                </Typography>
                                <IconButton size="small">
                                    {statsOpen ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                            {statsLoading && <LinearProgress />}
                            <Collapse in={statsOpen}>
                                <Grid container>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Box sx={{ px: 2, pb: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                                <CategoryIcon fontSize="inherit" /> By Category
                                            </Typography>
                                            <List dense disablePadding>
                                                {stats.category_breakdown.slice(0, 6).map((item) => (
                                                    <ListItem key={item.label} disablePadding sx={{ py: 0.25 }}>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                    <Typography variant="body2">{item.label}</Typography>
                                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                                        <Typography variant="body2" fontWeight={600}>{formatCurrencyBDT(item.value)}</Typography>
                                                                        <Chip label={`${item.percentage}%`} size="small" sx={{ height: 18, fontSize: "0.7rem" }} />
                                                                    </Box>
                                                                </Box>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Box sx={{ px: 2, pb: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                                <Payment fontSize="inherit" /> By Payment Method
                                            </Typography>
                                            <List dense disablePadding>
                                                {stats.payment_method_breakdown.map((item) => (
                                                    <ListItem key={item.label} disablePadding sx={{ py: 0.25 }}>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                    <Typography variant="body2">{item.label}</Typography>
                                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                                        <Typography variant="body2" fontWeight={600}>{formatCurrencyBDT(item.value)}</Typography>
                                                                        <Chip label={`${item.percentage}%`} size="small" sx={{ height: 18, fontSize: "0.7rem" }} />
                                                                    </Box>
                                                                </Box>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </Paper>
                    </Box>
                )}

                {/* Filter bar */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    {/* Date presets */}
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
                        {presets.map((p) => (
                            <Chip
                                key={p.key}
                                label={p.label}
                                size="small"
                                onClick={() => handlePreset(p.key)}
                                color={datePreset === p.key ? "primary" : "default"}
                                variant={datePreset === p.key ? "filled" : "outlined"}
                            />
                        ))}
                    </Box>

                    <Grid container spacing={1.5} alignItems="center">
                        {/* Text search */}
                        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search vendor, notes…"
                                defaultValue={searchText}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Category multi-select */}
                        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="Category"
                                value={selectedCategories[0] ?? ""}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSelectedCategories(val ? [val] : []);
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                SelectProps={{ displayEmpty: true }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {allCategories.map((c) => (
                                    <MenuItem key={c.category_id} value={c.category_id}>
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Payment method */}
                        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="Payment Method"
                                value={paymentMethod}
                                onChange={(e) => {
                                    setPaymentMethod(e.target.value as PaymentMethod | "");
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                SelectProps={{ displayEmpty: true }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="">All Methods</MenuItem>
                                {EXPENSE_PAYMENT_METHODS.map((m) => (
                                    <MenuItem key={m} value={m}>{m}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Custom date range (visible when "all" or if user wants fine-grained) */}
                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <DatePicker
                                label="From"
                                value={dateRange.from}
                                onChange={(d) => {
                                    setDatePreset("all");
                                    setCustomRange((r) => ({ ...r, from: d }));
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <DatePicker
                                label="To"
                                value={dateRange.to}
                                onChange={(d) => {
                                    setDatePreset("all");
                                    setCustomRange((r) => ({ ...r, to: d }));
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        </Grid>

                        {/* Clear filters */}
                        {hasActiveFilters && (
                            <Grid size={{ xs: 12, sm: "auto" }}>
                                <Tooltip title="Clear all filters">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<Clear />}
                                        onClick={handleClearFilters}
                                    >
                                        Clear
                                    </Button>
                                </Tooltip>
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* Active filter chips */}
                {(selectedCategories.length > 0 || paymentMethod) && (
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
                        {selectedCategories.map((id) => {
                            const cat = allCategories.find((c) => c.category_id === id);
                            return cat ? (
                                <Chip
                                    key={id}
                                    label={cat.name}
                                    size="small"
                                    onDelete={() => handleCategoryToggle(id)}
                                    color="primary"
                                    variant="outlined"
                                />
                            ) : null;
                        })}
                        {paymentMethod && (
                            <Chip
                                label={paymentMethod}
                                size="small"
                                onDelete={() => setPaymentMethod("")}
                                color="secondary"
                                variant="outlined"
                            />
                        )}
                    </Box>
                )}

                {/* Table */}
                <ServerSideDataTable
                    rows={expenses}
                    columns={columns}
                    loading={loading}
                    totalRows={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowId={(row: Expense) => row.expense_id}
                    onEdit={(id: string) => {
                        const exp = expenses.find((e) => e.expense_id === id);
                        if (exp) handleEditExpense(exp);
                    }}
                    onDelete={handleDeleteExpense}
                />

                {/* Dialogs */}
                <ExpenseForm
                    open={formOpen}
                    onClose={() => { setFormOpen(false); setSelectedExpense(null); }}
                    onSuccess={handleFormSuccess}
                    expense={selectedExpense}
                    mode={formMode}
                />

                <ExpenseCategoryManager
                    open={categoryManagerOpen}
                    onClose={() => setCategoryManagerOpen(false)}
                />
            </Box>
        </LocalizationProvider>
    );
};
